import { z } from "zod";
import { Ingest } from "./contracts.js";

/**
 * Alert webhooks from other monitoring tools. Each tool posts to
 * `${Ingest.baseUrl}${Ingest.hooksPath}/<source>/<ingest key>` (or the key as a Bearer/Basic header,
 * an `X-Pagerless-Key` header, or `?key=`). A firing alert becomes a "custom" event whose
 * fingerprint is the tool's own alert id, so one alert is one problem; the tool's "resolved"
 * becomes a "resolve" event that closes it.
 */
export const HookSource = z.enum([
  "alertmanager",
  "grafana",
  "datadog",
  "newrelic",
  "gcp",
  "azure",
  "sentry",
  "betterstack",
  "uptimerobot",
  "pingdom",
  "checkly",
  "stripe",
  "github",
  "generic",
]);
export type HookSource = z.infer<typeof HookSource>;

/**
 * How each tool proves a delivery came from it.
 * - `provider`: the tool makes the signing secret (Stripe whsec_…, Sentry client secret). Save it
 *   for the key; once saved, unsigned or badly signed deliveries are refused.
 * - `chosen`: you pick the secret in the tool. Paste the ingest key (or save your own); a signed
 *   delivery is always checked.
 * - `none`: the tool does not sign; the key in the URL is the proof (a Pub/Sub push OIDC token, when
 *   sent, is still checked).
 */
export type HookSigning = "provider" | "chosen" | "none";

export type HookSourceInfo = {
  source: HookSource;
  /** The tool's name, as people know it. */
  name: string;
  signing: HookSigning;
  /** One short line: where in the tool the URL goes. */
  where: string;
};

export const HookSources: readonly HookSourceInfo[] = [
  {
    source: "alertmanager",
    name: "Prometheus Alertmanager",
    signing: "none",
    where: "alertmanager.yml, webhook_configs url",
  },
  { source: "grafana", name: "Grafana alerts", signing: "chosen", where: "Contact point, Webhook, URL" },
  {
    source: "datadog",
    name: "Datadog monitors",
    signing: "none",
    where: "Webhooks integration, URL (use the payload below)",
  },
  { source: "newrelic", name: "New Relic", signing: "none", where: "Workflows, Webhook destination, URL" },
  {
    source: "gcp",
    name: "Google Cloud Monitoring",
    signing: "none",
    where: "Notification channel, Webhook or Pub/Sub push",
  },
  { source: "azure", name: "Azure Monitor", signing: "none", where: "Action group, Webhook, common alert schema on" },
  {
    source: "sentry",
    name: "Sentry",
    signing: "provider",
    where: "Internal integration, Webhook URL, Alert rule action",
  },
  { source: "betterstack", name: "Better Stack", signing: "none", where: "Integrations, Webhook, URL" },
  { source: "uptimerobot", name: "UptimeRobot", signing: "none", where: "Alert contact, Webhook, URL" },
  { source: "pingdom", name: "Pingdom", signing: "none", where: "Integrations, Webhook, URL" },
  { source: "checkly", name: "Checkly", signing: "chosen", where: "Alert channel, Webhook, URL" },
  {
    source: "stripe",
    name: "Stripe failed payments",
    signing: "provider",
    where: "Developers, Webhooks, endpoint URL",
  },
  { source: "github", name: "GitHub Actions", signing: "chosen", where: "Repo settings, Webhooks, workflow runs" },
  { source: "generic", name: "Any tool (JSON)", signing: "chosen", where: "POST the JSON shape below" },
];

/** The URL to paste into a tool. The key is in the path because many tools cannot set headers. */
export function hookUrl(source: HookSource, ingestKey: string, baseUrl: string = Ingest.baseUrl): string {
  return `${baseUrl}${Ingest.hooksPath}/${source}/${encodeURIComponent(ingestKey)}`;
}

/** Severity words a tool may send; mapped to wake/tell/log by ingest (unknown → tell). */
export const GenericSeverity = z.string().trim().max(40);

/**
 * The generic JSON webhook: POST one alert, or `{"alerts": [...]}`, to /v1/hooks/generic.
 * `id` is your alert's own id: the same id again while firing adds to the same problem, and
 * `status: "resolved"` with that id closes it. Optional `X-Pagerless-Signature: sha256=<hex>` is an
 * HMAC-SHA256 of the body with the ingest key (or a saved secret).
 */
export const GenericAlert = z.object({
  id: z.string().trim().min(1).max(200),
  status: z.enum(["firing", "resolved"]).default("firing"),
  /** Required while firing: one short line ("Disk almost full on db-1"). Long text is cut, not refused. */
  title: z.string().trim().max(500).optional(),
  message: z.string().max(10_000).optional(),
  /** wake | tell | log, or the tool's own word (critical, warning, info, P1…). Default tell. */
  severity: GenericSeverity.optional(),
  service: z.string().trim().min(1).max(100).optional(),
  env: z.string().trim().min(1).max(40).optional(),
  url: z.string().max(2000).optional(),
  tags: z.record(z.string().max(64), z.string().max(256)).optional(),
  /** When it happened: epoch ms or ISO 8601. Default: when it arrived. */
  at: z.union([z.number().int().nonnegative(), z.string().max(40)]).optional(),
});
export type GenericAlert = z.infer<typeof GenericAlert>;

export const GenericAlertBatch = z.object({ alerts: z.array(GenericAlert).min(1).max(100) });
export type GenericAlertBatch = z.infer<typeof GenericAlertBatch>;

/**
 * Payload templates for tools that let you write the webhook body. Ingest reads these field names
 * (cloud/ingest/src/hooks); the web page and the connect CLI print them.
 */
export const HookPayloads = {
  /** Datadog Webhooks integration, "Payload". */
  datadog:
    '{"id":"$ID","alert_id":"$ALERT_ID","scope":"$ALERT_SCOPE","transition":"$ALERT_TRANSITION","priority":"$ALERT_PRIORITY","title":"$EVENT_TITLE","body":"$EVENT_MSG","date":"$DATE","link":"$LINK","tags":"$TAGS","host":"$HOSTNAME"}',
  /** UptimeRobot webhook alert contact, "POST value (JSON)". */
  uptimerobot:
    '{"monitorID":"*monitorID*","monitorURL":"*monitorURL*","monitorFriendlyName":"*monitorFriendlyName*","alertType":"*alertType*","alertTypeFriendlyName":"*alertTypeFriendlyName*","alertDetails":"*alertDetails*","alertDateTime":"*alertDateTime*"}',
} as const;
